import {
  ModalDialog,
  ModalMessage,
  ModalMessageColorsEnum,
  WarningInfoIcon,
} from '@acceso/design-system';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { EnrichmentsSelector } from '@/components/EnrichmentsSelector/EnrichmentsSelector';
import { useNotificationStore } from '@/stores/notifications';

type TEnrichmentsSelection = {
  stt: boolean;
  logos: boolean;
  peoples: boolean;
  ocr: boolean;
  advertising: boolean;
  overwrite_detections: boolean;
};

type RelaunchEnrichmentsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onAction: (enrichments: TEnrichmentsSelection) => void;
  allowAdvertising?: boolean;
  heading?: string;
  actionLabel?: string;
  isRadio?: boolean;
};

export function RelaunchEnrichmentsModal({
  isOpen,
  onClose,
  onAction,
  allowAdvertising = true,
  heading,
  actionLabel,
  isRadio = false,
}: RelaunchEnrichmentsModalProps) {
  const { t } = useTranslation();
  const { addErrorNotification } = useNotificationStore();
  const form = useForm<TEnrichmentsSelection>({
    defaultValues: {
      ocr: false,
      stt: false,
      logos: false,
      peoples: false,
      advertising: false,
      overwrite_detections: false,
    },
  });

  const { watch, reset } = form;
  const values = watch();
  const isAnySelected = useMemo(
    () => values.ocr || values.stt || values.logos || values.peoples || values.advertising,
    [values]
  );

  useEffect(() => {
    reset();
  }, [isOpen]);

  const handleAction = () => {
    if (!isAnySelected) {
      addErrorNotification(t('Error'), t('Selecciona al menos un enriquecimiento'));

      return;
    }
    const selected: TEnrichmentsSelection = {
      ocr: !!values.ocr,
      stt: !!values.stt,
      logos: !!values.logos,
      peoples: !!values.peoples,
      advertising: allowAdvertising ? !!values.advertising : false,
      overwrite_detections: !!values.overwrite_detections,
    };
    onAction(selected);
  };

  const computedActionLabel = actionLabel ?? t('Lanzar procesos');

  return (
    <ModalDialog
      isOpen={isOpen}
      onClose={onClose}
      onAction={handleAction}
      heading={heading ?? t('Relanzar procesos de enriquecimiento')}
      actionLabel={computedActionLabel}
      linkLabel={t('Cancelar')}
      color={ModalMessageColorsEnum.WARNING}
    >
      <ModalMessage
        title={t('¿Estás seguro de querer voler a ejecutar los procesos de enriquecimiento?')}
        icon={WarningInfoIcon}
      />
      <div className="mt-4">
        <EnrichmentsSelector form={form} allowAdvertising={allowAdvertising} isRadio={isRadio} />
      </div>
    </ModalDialog>
  );
}
